import React, { useEffect, useState } from 'react';
import { getInventory } from '../api/gameBuilderAPI';
import ItemEditor from './ItemEditor';

/**
 * The item inventory (backend inventory/<id>.yaml): every item a player can use in a game - what it's for, its
 * states and the actions it offers - and "Try it", which plays the item's own sample mission (standard data
 * from its file) in the real game player. This is where an item's mechanics are judged before they appear in
 * a mission. Items are added as files (see inventory/README.md), or made from a mission's new objects (marked To
 * review). An item that keeps to the item schema can be edited here (ItemEditor); one with code of its own - a drawn
 * view, a document, spots on its picture - says why not, and is changed in its file. Search finds an item by its name,
 * what it's for, the other names it goes by, or a setting.
 */
const MECHANIC = { confirm: '👆 button', choose: '☑️ pick one', amount: '⚖️ measure' };
const PAGE_SIZE = 10;
const MAX_TOGETHER = 10;
const PRESET = 5;              // "Try together" starts with the first five on the page ticked
// Item pictures are served by the backend (as the game's are) - see MissionGame.
const API_BASE = process.env.REACT_APP_API_URL || '';
const asset = (src) => (src && src.startsWith('/game-builder-assets') ? `${API_BASE}${src}` : src);

export default function InventoryCatalog({ onClose }) {
  const [items, setItems] = useState(null);
  const [error, setError] = useState('');
  const [page, setPage] = useState(0);
  const [picked, setPicked] = useState(null);   // ids to try together (null: the page's first five)
  // items made from a mission's new objects (backend game.promote_new_objects) are marked to be reviewed
  const [onlyReview, setOnlyReview] = useState(false);
  const [onlyEditable, setOnlyEditable] = useState(false);
  const [query, setQuery] = useState('');
  const [choices, setChoices] = useState({});
  const [editing, setEditing] = useState(null);     // the item being edited
  const [saved, setSaved] = useState('');
  const toReview = (items || []).filter((it) => it.review);
  const words = query.trim().toLowerCase().split(/\s+/).filter(Boolean);
  const found = (it) => words.every((w) => [it.id, it.name, it.for, ...(it.aka || []), ...(it.tags || [])].join(' ').toLowerCase().includes(w));
  const listed = (items || []).filter((it) => (!onlyReview || it.review) && (!onlyEditable || it.form) && found(it));
  const pages = Math.max(1, Math.ceil(listed.length / PAGE_SIZE));
  const shown = listed.slice(page * PAGE_SIZE, (page + 1) * PAGE_SIZE);
  const tryable = (it) => it.id !== 'patient' && it.problems.length === 0;
  const together = picked ?? shown.filter(tryable).slice(0, PRESET).map((it) => it.id);
  const toggle = (id) => setPicked((p) => {
    const now = p ?? together;
    return now.includes(id) ? now.filter((x) => x !== id) : now.length < MAX_TOGETHER ? [...now, id] : now;
  });
  // Trying out opens its own tab (/game-builder/try - GamePages.jsx), so the catalog stays as it was.
  const openTry = (ids) => window.open(`/game-builder/try?items=${ids.map(encodeURIComponent).join(',')}`, '_blank', 'noopener');
  const pager = items && listed.length > PAGE_SIZE && (
    <nav className="gb-pager" aria-label="Pages">
      <button type="button" className="gb-btn gb-btn-quiet gb-btn-sm" disabled={page === 0} onClick={() => { setPage((p) => p - 1); setPicked(null); }}>‹ Previous</button>
      <span className="gb-muted gb-small">Items {page * PAGE_SIZE + 1}–{Math.min(listed.length, (page + 1) * PAGE_SIZE)} of {listed.length}</span>
      <button type="button" className="gb-btn gb-btn-quiet gb-btn-sm" disabled={page >= pages - 1} onClick={() => { setPage((p) => p + 1); setPicked(null); }}>Next ›</button>
    </nav>
  );
  useEffect(() => {
    getInventory().then((d) => { setItems(d.items); setChoices(d.choices || {}); }).catch((e) => setError(e.message));
  }, []);
  const filter = (fn) => { fn(); setPage(0); setPicked(null); };

  if (editing) {
    return (
      <div className="gb-catalog">
        <ItemEditor item={editing} choices={choices} itemIds={items.map((it) => it.id)} onCancel={() => setEditing(null)}
          onSaved={(entry) => {
            setItems((all) => all.map((it) => (it.id === entry.id ? entry : it)));
            setSaved(`Saved ${entry.name} (v${entry.version}).`);
            setEditing(null);
          }} />
      </div>
    );
  }

  return (
    <div className="gb-catalog">
      <header className="gb-catalog-head">
        <div>
          <h2>Item inventory</h2>
          <p className="gb-muted gb-small">Every item a player uses in a simulation comes from here. Try each one with its standard data.</p>
        </div>
        <button type="button" className="gb-btn gb-btn-quiet" onClick={onClose}>Back to plans</button>
      </header>
      {error && <div className="gb-error" role="alert"><span>{error}</span></div>}
      {!items && !error && <p className="gb-muted"><span className="gb-spinner" /> Loading the inventory…</p>}
      {saved && <p className="gb-ie-ok" role="status">{saved}</p>}
      {items && (
        <div className="gb-catalog-find">
          <input type="search" aria-label="Search the inventory" placeholder="Search by name, what it's for, or a setting (kitchen, warehouse…)"
            value={query} onChange={(e) => filter(() => setQuery(e.target.value))} />
          <button type="button" className={`gb-btn gb-btn-sm ${onlyEditable ? '' : 'gb-btn-quiet'}`} aria-pressed={onlyEditable}
            onClick={() => filter(() => setOnlyEditable((v) => !v))}>✏️ Editable only</button>
          <span className="gb-small gb-muted" role="status">{listed.length} of {items.length} items</span>
        </div>
      )}
      {items && (
        <div className="gb-together">
          <button type="button" className="gb-btn" disabled={together.length < 2} onClick={() => openTry(together)}>
            ▶ Try together ({together.length})
          </button>
          <button type="button" className="gb-btn gb-btn-quiet" disabled={!together.length} onClick={() => setPicked([])}>Deselect all</button>
          <span className="gb-small gb-muted">Tick up to {MAX_TOGETHER} items below to try them side by side in one scene.</span>
          {toReview.length > 0 && (
            <button type="button" className={`gb-btn gb-btn-sm ${onlyReview ? '' : 'gb-btn-quiet'}`} aria-pressed={onlyReview}
              onClick={() => filter(() => setOnlyReview((v) => !v))}>
              🔎 To review ({toReview.length})
            </button>
          )}
        </div>
      )}
      {pager}
      {items && listed.length === 0 && <p className="gb-muted">No item matches - try another word, or clear the filters.</p>}
      <div className="gb-catalog-grid">
        {shown.map((it) => (
          <section key={it.id} className="gb-card gb-catalog-card">
            <div className="gb-catalog-picture">{it.picture ? <img src={asset(it.picture)} alt="" /> : <span>{it.icon}</span>}</div>
            <h3>{it.name} <span className="gb-muted gb-small">v{it.version}</span>
              {it.review && <span className="gb-tag gb-review" title="Made from a mission's new object - check it">To review</span>}</h3>
            {tryable(it) && (
              <label className="gb-small gb-inline">
                <input type="checkbox" checked={together.includes(it.id)} onChange={() => toggle(it.id)}
                  disabled={!together.includes(it.id) && together.length >= MAX_TOGETHER} /> Try together
              </label>
            )}
            <p className="gb-small">{it.for}</p>
            <ol className="gb-catalog-states">
              {it.states.map((st) => (
                <li key={st.name}>
                  <strong>{st.name}</strong>{st.start ? ' (start)' : ''}{st.effect && st.effect !== 'none' ? ` · ${st.effect}s` : ''}
                  {st.conveys && <span className="gb-muted"> - “{st.conveys}”</span>}
                  {st.actions.length > 0 && (
                    <ul>{st.actions.map((a) => (
                      <li key={a.id}>{MECHANIC[a.mechanic] || a.mechanic}: <em>{a.label}</em>{a.to ? ` → ${a.to}` : ''}{a.records ? ` · records ${a.records}` : ''}</li>
                    ))}</ul>
                  )}
                </li>
              ))}
            </ol>
            <p className="gb-small gb-muted">{(it.tags || []).join(' · ')}</p>
            {it.problems.length > 0 && <p className="gb-error gb-small">{it.problems[0]}</p>}
            <div className="gb-card-actions">
              {it.problems.length === 0 && <button type="button" className="gb-btn" onClick={() => openTry([it.id])}>▶ Try it</button>}
              <button type="button" className="gb-btn gb-btn-quiet" disabled={!it.form} title={it.not_editable || `Edit ${it.name}`}
                aria-label={`Edit ${it.name}`} onClick={() => { setSaved(''); setEditing(it); }}>✏️ Edit</button>
            </div>
            {!it.form && it.not_editable && <p className="gb-small gb-muted">Can&rsquo;t be edited here: {it.not_editable.replace(/^It /, 'it ')}</p>}
          </section>
        ))}
      </div>
      {pager}
    </div>
  );
}
